import { apiFetch, apiFetchJson } from '@wundo/client/api.ts';
import type { Cohort, TrackParticipant } from '@/types/cohorts.ts';
import type { PaginatedResponse } from '@/types/pagination.ts';
import type { Track, TrackDocument } from '@/types/tracks.ts';

/**
 * A track as the list endpoint sends it: the record plus the organisation it
 * belongs to, which the detail endpoint embeds as well.
 */
export interface TrackWithOrganisation extends Track {
  organisation: {
    id: string;
    name: string;
    type: string;
  };
}

/** What the create and edit form sends. Every field is optional on an edit. */
export interface TrackInput {
  title?: string;
  organisation_id?: string;
  welcome_skill_id?: string | null;
  home_skill_id?: string | null;
  topic_set_id?: string | null;
  content?: string | null;
}

export interface ListTracksOptions {
  page: number;
  limit: number;
  /** Only this organisation's tracks. Omitted, a superadmin sees every one. */
  organisationId?: string;
  /** Without one the server answers alphabetically by title. */
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export async function listTracks({
  page,
  limit,
  organisationId,
  sortBy,
  sortOrder,
}: ListTracksOptions): Promise<PaginatedResponse<TrackWithOrganisation>> {
  const params = new URLSearchParams({ page: String(page), limit: String(limit) });
  if (organisationId) {
    params.append('organisation_id', organisationId);
  }
  if (sortBy) {
    params.set('sort_by', sortBy);
    params.set('sort_order', sortOrder ?? 'asc');
  }

  return apiFetchJson<PaginatedResponse<TrackWithOrganisation>>(`/tracks?${params}`);
}

export async function getTrack(trackId: string): Promise<TrackWithOrganisation> {
  return apiFetchJson<TrackWithOrganisation>(`/tracks/${trackId}`);
}

export async function createTrack(input: TrackInput): Promise<Track> {
  return apiFetchJson<Track>('/tracks', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function updateTrack(trackId: string, input: TrackInput): Promise<Track> {
  return apiFetchJson<Track>(`/tracks/${trackId}`, {
    method: 'PUT',
    body: JSON.stringify(input),
  });
}

export async function deleteTrack(trackId: string): Promise<void> {
  await apiFetch(`/tracks/${trackId}`, { method: 'DELETE' });
}

/** Everyone enrolled in this track, through whichever cohort they joined. */
export async function listTrackParticipants(
  trackId: string,
): Promise<TrackParticipant[]> {
  const { data } = await apiFetchJson<{ data: TrackParticipant[] }>(
    `/tracks/${trackId}/participants`,
  );
  return data;
}

export interface ListTrackCohortsOptions {
  trackId: string;
  page: number;
  limit: number;
}

export async function listTrackCohorts({
  trackId,
  page,
  limit,
}: ListTrackCohortsOptions): Promise<PaginatedResponse<Cohort>> {
  const params = new URLSearchParams({
    track_id: trackId,
    page: String(page),
    limit: String(limit),
  });

  return apiFetchJson<PaginatedResponse<Cohort>>(`/cohorts?${params}`);
}

/** The pivot rows saying which documents this track teaches from. */
export async function listTrackDocuments(trackId: string): Promise<TrackDocument[]> {
  return apiFetchJson<TrackDocument[]>(`/track-documents?track_id=${trackId}`);
}

export async function linkTrackDocument(
  trackId: string,
  documentId: string,
): Promise<TrackDocument> {
  return apiFetchJson<TrackDocument>('/track-documents', {
    method: 'POST',
    body: JSON.stringify({ track_id: trackId, document_id: documentId }),
  });
}

export async function unlinkTrackDocument(trackDocumentId: string): Promise<void> {
  await apiFetch(`/track-documents/${trackDocumentId}`, { method: 'DELETE' });
}
